// The lead agent's plan, reviewed before anything is searched (as Explee's campaign brief): each segment's problem,
// who to write to, the criteria every lead is scored against, what rules a lead out, and clients like the ones
// wanted. Unticked segments are not searched; edits go into the search and the ranking.
import { useTranslate } from "ra-core";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import type { Segment } from "../../../../../supabase/functions/_shared/leadAgent.ts";

const lines = (value: string) =>
  value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
// a location is "City, Country", so locations are separated by semicolons
const semicolons = (value: string) =>
  value
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean);
const commas = (value: string) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

export const SegmentReview = ({
  segments,
  chosen,
  disabled,
  onToggle,
  onEdit,
}: {
  segments: Segment[];
  chosen: Set<number>;
  disabled: boolean;
  onToggle: (index: number) => void;
  onEdit: (index: number, segment: Segment) => void;
}) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.leads.agent.brief.${key}`);
  return (
    <ul className="flex flex-col gap-3">
      {segments.map((segment, i) => {
        const set = (patch: Partial<Segment>) =>
          onEdit(i, { ...segment, ...patch });
        return (
          <li
            key={i}
            className={`rounded-md border p-3 flex flex-col gap-2 ${chosen.has(i) ? "" : "opacity-60"}`}
          >
            <div className="flex items-center gap-3">
              <Checkbox
                aria-label={segment.name}
                checked={chosen.has(i)}
                disabled={disabled}
                onCheckedChange={() => onToggle(i)}
              />
              <Input
                aria-label={t("name")}
                className="font-medium"
                value={segment.name}
                disabled={disabled}
                onChange={(e) => set({ name: e.target.value })}
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {segment.fit}%
              </span>
            </div>
            {chosen.has(i) && (
              <div className="grid gap-2 md:grid-cols-2 text-sm">
                <Field label={t("problem")}>
                  <Textarea
                    className="min-h-16"
                    value={segment.customer_problem}
                    disabled={disabled}
                    onChange={(e) => set({ customer_problem: e.target.value })}
                  />
                </Field>
                <Field label={t("why")}>
                  <Textarea
                    className="min-h-16"
                    value={segment.why}
                    disabled={disabled}
                    onChange={(e) => set({ why: e.target.value })}
                  />
                </Field>
                <Field label={t("positive")}>
                  <Textarea
                    className="min-h-20"
                    value={segment.positive_criteria.join("\n")}
                    disabled={disabled}
                    onChange={(e) =>
                      set({ positive_criteria: lines(e.target.value) })
                    }
                  />
                </Field>
                <Field label={t("negative")}>
                  <Textarea
                    className="min-h-20"
                    value={segment.negative_criteria.join("\n")}
                    disabled={disabled}
                    onChange={(e) =>
                      set({ negative_criteria: lines(e.target.value) })
                    }
                  />
                </Field>
                <Field label={t("role")}>
                  <Input
                    value={segment.target_role}
                    disabled={disabled}
                    onChange={(e) => set({ target_role: e.target.value })}
                  />
                </Field>
                <Field label={t("examples")}>
                  <Input
                    value={segment.example_clients.join(", ")}
                    disabled={disabled}
                    onChange={(e) =>
                      set({ example_clients: commas(e.target.value) })
                    }
                  />
                </Field>
                <Field label={t("keywords")}>
                  <Input
                    value={segment.keywords.join(", ")}
                    disabled={disabled}
                    onChange={(e) => set({ keywords: commas(e.target.value) })}
                  />
                </Field>
                <Field label={t("locations")}>
                  <Input
                    value={segment.locations.join("; ")}
                    disabled={disabled}
                    onChange={(e) =>
                      set({ locations: semicolons(e.target.value) })
                    }
                  />
                </Field>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <label className="flex flex-col gap-1">
    <span className="text-xs text-muted-foreground">{label}</span>
    {children}
  </label>
);
